/**
 * S-09 Onboarding — the Reject-Match briefing (V1 auth-profiles-gdpr).
 * The identity moment (PRD §2.2): a MANDATORY 3-card interstitial —
 *   1 "swipe right = like" · 2 "swipe left = reject" + the reject-visibility
 *   disclosure (©onboard.briefing_reject_note) · 3 the REJECT-MATCH reveal
 * (©onboard.reject_match_reveal) — ending with per-category notification
 * opt-ins and the tone waiver (©onboard.tone_waiver). Completing card 3
 * submits the server-persisted ack (briefing_ack_at) that gates
 * ONBOARDING → ACTIVE; no client skips it.
 *
 * All-done-but-photo-in-review: the ack stores normally and the screen
 * resolves to the ©moderation.onboarding_pending_gate waiting state, polling
 * /onboarding/status — activation fires server-side on approval (SM-13d
 * PHOTO_LIVE fan-out); a rejection routes back to S-07.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { copy, resolveCopy } from "@humble/shared";
import {
  Button,
  Card,
  glitchVariants,
  IconBrokenHeartFill,
  Mascot,
  pageVariants,
  toast,
} from "../../design/index.js";
import { api, isApiError, refreshSession } from "../../lib/api.js";
import { OnboardingFrame } from "./onboarding-frame.js";
import "./onboarding.css";
import "../auth/auth.css"; // .auth__serious (shared V1 primitive)

interface OnboardingStatusWire {
  lifecycle?: string;
  guards?: {
    photo_live?: boolean;
    photo_pending_review?: boolean;
    identity?: boolean;
    prompt?: boolean;
    location?: boolean;
    briefing_ack?: boolean;
  };
}

type Phase = "cards" | "waiting" | "photo_rejected";

/** Human labels for the opt-out notification categories (S-27 mirror). */
const CATEGORY_LABELS: Record<string, string> = {
  match_classic: "Classic matches",
  match_reject: "Reject-Matches",
  message: "Messages",
  crush: "Crushes",
  sr_landed: "Super Reject landed",
  shield_block: "Shield blocks",
  streak: "Killstreaks",
  takeover: "Enemy Takeovers",
  match_expiry: "Match expiry warnings",
  cooldown_return: "Cooldown over",
  roulette: "Roulette",
  pity: "Condolences Protocol",
  call: "Calls",
  run_it_back: "Run It Back offers",
  product: "Humble news",
};

export function BriefingScreen() {
  const navigate = useNavigate();
  const [card, setCard] = useState<1 | 2 | 3>(1);
  const [phase, setPhase] = useState<Phase>("cards");
  const [busy, setBusy] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Prefill the notification toggles from /settings (defaults are ON).
  useEffect(() => {
    let mounted = true;
    void api
      .get("/settings")
      .then((s) => {
        const wire = (s as { notifications?: Record<string, boolean> }).notifications;
        if (mounted && wire) setNotifPrefs(wire);
      })
      .catch(() => {
        if (mounted) {
          setNotifPrefs(Object.fromEntries(Object.keys(CATEGORY_LABELS).map((k) => [k, true])));
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  async function finishToDeck() {
    await refreshSession(); // pick up lifecycle=active on the session store
    navigate("/deck");
  }

  function startPolling() {
    setPhase("waiting");
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const status = (await api.get("/onboarding/status")) as OnboardingStatusWire;
          if (status.lifecycle === "active") {
            if (pollRef.current) clearInterval(pollRef.current);
            toast(copy.moderation.onboarding_approved, "success");
            await finishToDeck();
          } else if (!status.guards?.photo_live && !status.guards?.photo_pending_review) {
            if (pollRef.current) clearInterval(pollRef.current);
            setPhase("photo_rejected");
          }
        } catch {
          /* poll again */
        }
      })();
    }, 2500);
  }

  async function onAck() {
    if (busy) return;
    setBusy(true);
    try {
      // Persist the notification choices first (best-effort — the ack is the guard).
      try {
        await api.patch("/settings", { body: { notifications: notifPrefs } });
      } catch {
        /* per-category page in settings remains available */
      }
      const status = (await api.post("/onboarding/briefing-ack")) as OnboardingStatusWire;
      if (status.lifecycle === "active") {
        await finishToDeck();
      } else if (status.guards?.photo_pending_review) {
        startPolling();
      } else if (!status.guards?.photo_live) {
        setPhase("photo_rejected");
      } else {
        // some other guard missing (deep-linked here early) — send them to it
        if (!status.guards?.identity) navigate("/onboard/identity");
        else if (!status.guards?.prompt) navigate("/onboard/profile");
        else if (!status.guards?.location) navigate("/onboard/location");
        else startPolling();
      }
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <OnboardingFrame step={4} screen="S-09">
      <Card padding="lg" className={phase === "cards" && card === 3 ? "crt crt--glow" : undefined}>
        {phase === "cards" && (
          <motion.div
            className="briefing__card"
            data-testid={`briefing-card-${card}`}
            key={card}
            variants={card === 3 ? glitchVariants : pageVariants}
            initial="initial"
            animate="enter"
          >
            {card === 1 && (
              <>
                <div className="briefing__glyph briefing__glyph--like" aria-hidden="true">
                  <span>♥</span>
                  <span style={{ fontSize: "0.55em", opacity: 0.7 }}>→ ←</span>
                  <span>♥</span>
                </div>
                <h1 className="briefing__title">Swipe right = like</h1>
                <p className="text-dim">{copy.onboard.panels[1]}</p>
                <Button block variant="primary" size="lg" onClick={() => setCard(2)}>
                  Obviously
                </Button>
              </>
            )}
            {card === 2 && (
              <>
                <div className="briefing__glyph briefing__glyph--reject" aria-hidden="true">
                  <span>✕</span>
                  <span style={{ fontSize: "0.55em", opacity: 0.7 }}>→ ←</span>
                  <span>✕</span>
                </div>
                <h1 className="briefing__title">Swipe left = reject</h1>
                <p className="text-dim">{copy.onboard.panels[2]}</p>
                <p className="auth__serious" style={{ textAlign: "left" }}>
                  {copy.onboard.briefing_reject_note}
                </p>
                <Button block variant="primary" size="lg" onClick={() => setCard(3)}>
                  Understood
                </Button>
              </>
            )}
            {card === 3 && (
              <>
                <div className="briefing__glyph briefing__glyph--rm" aria-hidden="true">
                  <IconBrokenHeartFill width={44} height={44} />
                </div>
                <div className="briefing__revealwrap" data-text="MUTUAL REJECTION = MATCH">
                  <h1 className="briefing__title briefing__reveal">MUTUAL REJECTION = MATCH</h1>
                </div>
                <p data-testid="reject-match-reveal">{copy.onboard.reject_match_reveal}</p>

                <details style={{ textAlign: "left", margin: "10px 0" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                    Notifications — pick your poison
                  </summary>
                  <div className="briefing__toggles">
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <label key={key} className="briefing__toggle">
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={notifPrefs[key] ?? true}
                          onChange={(e) =>
                            setNotifPrefs((prev) => ({ ...prev, [key]: e.target.checked }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                  <p className="text-faint" style={{ fontSize: "0.8rem" }}>
                    {resolveCopy("notifications.mandatory_note")}
                  </p>
                </details>

                <p className="type-editorial text-dim" style={{ fontSize: "1rem" }}>
                  {copy.onboard.tone_waiver}
                </p>
                <Button
                  block
                  variant="primary"
                  size="lg"
                  loading={busy}
                  data-testid="briefing-ack"
                  onClick={() => void onAck()}
                >
                  Deal. Let me in.
                </Button>
              </>
            )}
            <div className="briefing__dots" aria-hidden="true">
              {[1, 2, 3].map((d) => (
                <span key={d} className={`briefing__dot${card >= d ? " briefing__dot--on" : ""}`} />
              ))}
            </div>
          </motion.div>
        )}

        {phase === "waiting" && (
          <div className="briefing__card" data-testid="briefing-waiting">
            <h1 className="briefing__title">Almost there</h1>
            <div style={{ display: "flex", justifyContent: "center", margin: "6px 0 10px" }}>
              <Mascot mood="loading" />
            </div>
            <p role="status">{copy.moderation.onboarding_pending_gate}</p>
          </div>
        )}

        {phase === "photo_rejected" && (
          <div className="briefing__card" data-testid="briefing-photo-rejected">
            <h1 className="briefing__title">About that photo…</h1>
            <p role="alert">
              {copy.moderation.photo_rejected.replace("{reason}", "it did not pass review")}
            </p>
            <Link to="/onboard/profile" className="btn btn--primary btn--block">
              Upload another photo
            </Link>
          </div>
        )}
      </Card>
    </OnboardingFrame>
  );
}
